import React, { useEffect, useState, useMemo, useRef } from 'react';
import { safeArray, unwrapListResponse } from '../../../components/ErrorBoundary';
import { useProjects } from '../../../context/ProjectsContext';
import LinkedCorrespondenceCard from '../../../components/LinkedCorrespondenceCard';
import ProjectFilterSelect from '../../../components/ProjectFilterSelect';
import ResetFiltersButton from '../../../components/ResetFiltersButton';
import TableEmptyRow from '../../../components/TableEmptyRow';
import TablePagination from '../../../components/TablePagination';
import StatusBadge from '../../../components/StatusBadge';
import { bankGuaranteesAPI } from '../../../services/authService';
import { banksAPI, bankLimitsAPI } from '../../../services/bgFinanceService';
import BgExportButtons from './BgExportButtons';
import { buildBgExportSubtitle, exportDataRowsCsv, exportDataRowsPdf } from './bgExportUtils';
import { ISSUE_MODE_OPTIONS, ISSUE_MODE_LABELS, RULE_SOURCE_LABELS, RENEWAL_STATUS_LABELS, renewalStatusTone, buildBgPayload } from './bgShared';
import BgRenewModal from './BgRenewModal';
import RenewalHistoryPopup, { RenewalHistorySection } from './RenewalHistoryPopup';
import { bankGuaranteesEnhancedAPI } from '../../../services/bgFinanceService';
import { toStoredAttachments, downloadAttachment, viewAttachmentInNewTab } from '../../../utils/attachmentStorage';
import { notifyError, notifySuccess, notifyWarning } from '../../../utils/notify';
import { confirmDialog } from '../../../utils/confirmDialog';
import { formatDateIN } from '../../../utils/dateFormat';
import { useFormConfiguration } from '../../../hooks/useFormConfiguration';
import { ConfiguredField } from '../../../components/ConfiguredField';
import { DynamicFieldsSection } from '../../../components/DynamicFieldRenderer';
import { adminAPI } from '../../../services/authService';

const BG_STATUS_CLASS_MAP = {
  Active: 'active',
  Expiring: 'expiring',
  'Renewal Requested': 'under-review',
  Renewed: 'approved',
  Released: 'released',
  Invoked: 'critical',
  Closed: 'closed',
};

const BG_TYPE_OPTIONS = ['Bank Guarantee', 'Insurance Bond'];
const PURPOSE_OPTIONS = [
  'Against Performance Guarantee',
  'Against Security Deposit',
  'Against With Held Amount',
  'Against with held amount for milestone',
  'Against Waterproofing, Facade, UPVC windows etc',
  'Against if anythor special items',
];

const formatCurrency = (val) => (
  `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(val)}`
);

const getDaysToExpiry = (bgOrDate) => {
  const expiryDate = typeof bgOrDate === 'object' ? (bgOrDate?.current_expiry_date || bgOrDate?.expiry_date) : bgOrDate;
  if (!expiryDate) return null;
  const diff = Math.ceil((new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24));
  return diff;
};

/** Normalize API row (list/create/update response) to client BG shape. */
const mapBgRow = (row) => {
  if (!row) return null;
  const payload = row.data && typeof row.data === 'object' ? row.data : {};
  const bankName = row.bank?.bank_name || row.bank_name_text || payload.bank || (typeof row.bank === 'string' ? row.bank : '-');
  return {
    id: row.id,
    project_id: row.project_id ?? row.projectId ?? 1,
    bg_type: row.bg_type_label || payload.bg_type || row.bg_type || 'Bank Guarantee',
    bg_no: row.bg_no || row.bgNo || payload.bg_no || `BG-${row.id}`,
    bank: bankName || '-',
    bank_id: row.bank_id ?? row.bankId ?? null,
    bank_limit_id: row.bank_limit_id ?? row.bankLimitId ?? null,
    ifsc: row.ifsc || payload.ifsc || row.bank?.ifsc_code || '-',
    branch: row.branch || payload.branch || row.bank?.branch_name || '-',
    amount: row.amount ?? payload.amount ?? 0,
    issue_date: row.issue_date || payload.issue_date || '',
    expiry_date: row.expiry_date || payload.expiry_date || '',
    claim_expiry_date: row.claim_expiry_date || payload.claim_expiry_date || '',
    status: row.status || payload.status || 'Active',
    issue_mode: row.issue_mode || row.issueMode || 'WITHOUT_LIMIT_TRACKING',
    counts_against_bank_limit: row.counts_against_bank_limit ?? row.countsAgainstBankLimit ?? false,
    counts_against_limit_rule_source: row.counts_against_limit_rule_source || row.countsAgainstLimitRuleSource || null,
    cash_margin_amount: row.cash_margin_amount ?? row.cashMarginAmount ?? 0,
    cash_margin_percent: row.cash_margin_percent ?? row.cashMarginPercent ?? null,
    beneficiary_name: row.beneficiary_name || row.drawn_in_favour_of || payload.drawn_in_favour_of || '',
    renewed_from_bg_id: payload.renewed_from_bg_id ?? row.renewed_from_bg_id ?? null,
    linked_correspondence: payload.linked_correspondence ?? row.linked_correspondence ?? null,
    attachments: payload.attachments ?? row.attachments ?? [],
    purpose: String(payload.purpose ?? row.purpose ?? '').trim(),
    drawn_in_favour_of: String(payload.drawn_in_favour_of ?? row.beneficiary_name ?? '').trim(),
    remarks: String(payload.remarks ?? row.remarks ?? '').trim(),
    renewal_count: row.renewal_count ?? row.renewalCount ?? 0,
    last_renewed_at: row.last_renewed_at || row.lastRenewedAt || '',
    last_renewed_by: row.last_renewed_by || row.lastRenewedBy || null,
    current_expiry_date: row.current_expiry_date || row.currentExpiryDate || row.expiry_date || '',
    renewal_status: row.renewal_status || row.renewalStatus || null,
    renewal_status_label: row.renewal_status_label || RENEWAL_STATUS_LABELS[row.renewal_status || row.renewalStatus] || null,
    days_to_expiry: row.days_to_expiry ?? null,
    can_renew: row.can_renew !== false && !['Closed','Released','Invoked'].includes(row.status || ''),
  };
};

const emptyNewBG = () => ({
  project_id: '',
  bg_type: 'Bank Guarantee',
  bg_no: '',
  amount: '',
  bank: '',
  ifsc: '',
  branch: '',
  issue_date: '',
  expiry_date: '',
  attachments: [],
  purpose: '',
  drawn_in_favour_of: '',
  remarks: '',
  bank_id: '',
  bank_limit_id: '',
  issue_mode: 'WITHOUT_LIMIT_TRACKING',
  counts_against_bank_limit: false,
  cash_margin_percent: '',
});

const emptyEditBG = () => ({
  id: null,
  project_id: '',
  bg_type: 'Bank Guarantee',
  bg_no: '',
  amount: '',
  bank: '',
  ifsc: '',
  branch: '',
  issue_date: '',
  expiry_date: '',
  claim_expiry_date: '',
  status: 'Active',
  attachments: [],
  linked_correspondence: null,
  renewed_from_bg_id: null,
  purpose: '',
  drawn_in_favour_of: '',
  remarks: '',
  bank_id: '',
  bank_limit_id: '',
  issue_mode: 'WITHOUT_LIMIT_TRACKING',
  counts_against_bank_limit: false,
  cash_margin_percent: '',
});

const BgBondRegister = ({ onViewThread }) => {
  const [projectFilter, setProjectFilter] = useState('all');
  const [bankFilter, setBankFilter] = useState('all');
  const [issueModeFilter, setIssueModeFilter] = useState('all');
  const [countsFilter, setCountsFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [kpiFilter, setKpiFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editBG, setEditBG] = useState(() => emptyEditBG());
  const [selectedBG, setSelectedBG] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);
  const [attachmentUploading, setAttachmentUploading] = useState(false);
  const [editAttachmentUploading, setEditAttachmentUploading] = useState(false);
  const { projects: projectOptions } = useProjects();
  const [dataReady, setDataReady] = useState(false);
  const [bankGuarantees, setBankGuarantees] = useState([]);
  const [banks, setBanks] = useState([]);
  const [bankLimits, setBankLimits] = useState([]);
  const [cashPolicy, setCashPolicy] = useState(null);
  const [renewTarget, setRenewTarget] = useState(null);
  const [historyPopup, setHistoryPopup] = useState(null);
  const [detailRenewals, setDetailRenewals] = useState([]);
  const [newBG, setNewBG] = useState(emptyNewBG);
  const [customValues, setCustomValues] = useState({});
  const [editCustomValues, setEditCustomValues] = useState({});
  const perPage = 10;
  const tableRef = useRef(null);
  const formConfig = useFormConfiguration({ pageKey: 'bank_guarantees', formKey: 'bg_create_edit', mode: showEditModal ? 'edit' : 'create' });
  const customFields = formConfig.customFields || [];

  const defaultsFromCustomFields = () => {
    const out = {};
    for (const f of customFields) {
      if (f.defaultValue != null && f.defaultValue !== '') out[f.fieldKey] = f.defaultValue;
      else if (f.fieldType === 'boolean') out[f.fieldKey] = false;
      else out[f.fieldKey] = '';
    }
    return out;
  };

  const onCustomChange = (fieldKey, value) => {
    setCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  };

  const onEditCustomChange = (fieldKey, value) => {
    setEditCustomValues((prev) => ({ ...prev, [fieldKey]: value }));
  };

  useEffect(() => {
    const loadBankGuarantees = async () => {
      try {
        const response = await bankGuaranteesAPI.getAll({ page: 1, pageSize: 100 });
        const { rows: rawRows } = unwrapListResponse(response);
        const rows = safeArray(rawRows).map((row) => mapBgRow(row)).filter(Boolean);
        if (rows.length > 0) setBankGuarantees(rows);
      } catch (error) {
        console.warn('BG / Insurance Bonds API unavailable, using local data:', error?.message || error);
      } finally {
        setDataReady(true);
      }
    };
    loadBankGuarantees();
    banksAPI.getAll().then((r) => setBanks(safeArray(r?.data?.data))).catch(() => setBanks([]));
  }, []);

  useEffect(() => {
    if (!newBG.bank_id && !editBG.bank_id) { setBankLimits([]); return; }
    const bankId = newBG.bank_id || editBG.bank_id;
    bankLimitsAPI.getAll({ bankId, isActive: 'true' }).then((r) => setBankLimits(safeArray(r?.data?.data))).catch(() => setBankLimits([]));
  }, [newBG.bank_id, editBG.bank_id]);

  useEffect(() => {
    if (!newBG.bank_limit_id && !editBG.bank_limit_id) { setCashPolicy(null); return; }
    const id = newBG.bank_limit_id || editBG.bank_limit_id;
    bankLimitsAPI.getFullCashPolicy(id).then((r) => setCashPolicy(r?.data || null)).catch(() => setCashPolicy(null));
  }, [newBG.bank_limit_id, editBG.bank_limit_id]);

  const projects = safeArray(projectOptions);

  useEffect(() => {
    if (!selectedBG?.id) { setDetailRenewals([]); return; }
    bankGuaranteesEnhancedAPI.getRenewals(selectedBG.id)
      .then((r) => setDetailRenewals(r?.data?.data || []))
      .catch(() => setDetailRenewals([]));
  }, [selectedBG?.id]);

  const canShowRenew = (bg) => bg?.can_renew !== false && !['Closed', 'Released', 'Invoked'].includes(bg?.status);

  const normalizeBGType = (bgType) => {
    const value = String(bgType || '').trim();
    if (
      value === 'PBG'
      || value === 'Performance BG'
      || value === 'Performance Bank Guarantee'
      || value === 'EMD'
      || value === 'Retention'
      || value === 'Advance'
      || value === 'Security BG'
      || value === 'Security Bank Guarantee'
      || value === 'Bank Guarantee'
    ) return 'Bank Guarantee';
    if (value === 'Insurance Bond' || value === 'Insurance') return 'Insurance Bond';
    return value || 'Bank Guarantee';
  };

  const filteredBGs = useMemo(() => {
    let bgs = [...safeArray(bankGuarantees)];
    if (projectFilter !== 'all') bgs = bgs.filter(b => b.project_id === parseInt(projectFilter));
    if (bankFilter !== 'all') bgs = bgs.filter(b => String(b.bank_id) === String(bankFilter));
    if (issueModeFilter !== 'all') bgs = bgs.filter(b => b.issue_mode === issueModeFilter);
    if (countsFilter === 'yes') bgs = bgs.filter(b => b.counts_against_bank_limit);
    if (countsFilter === 'no') bgs = bgs.filter(b => !b.counts_against_bank_limit);
    if (typeFilter !== 'all') bgs = bgs.filter(b => normalizeBGType(b.bg_type) === typeFilter);
    if (statusFilter !== 'all') bgs = bgs.filter(b => b.status === statusFilter);
    if (kpiFilter === 'expiring30') {
      bgs = bgs.filter((bg) => {
        const days = getDaysToExpiry(bg.expiry_date);
        return days >= 0 && days <= 30;
      });
    } else if (kpiFilter === 'expiring31to60') {
      bgs = bgs.filter((bg) => {
        const days = getDaysToExpiry(bg.expiry_date);
        return days > 30 && days <= 60;
      });
    } else if (kpiFilter === 'renewalRequested') {
      bgs = bgs.filter((bg) => bg.status === 'Renewal Requested');
    } else if (kpiFilter === 'expired') {
      bgs = bgs.filter((bg) => getDaysToExpiry(bg.expiry_date) < 0);
    }
    return bgs;
  }, [bankGuarantees, projectFilter, bankFilter, issueModeFilter, countsFilter, typeFilter, statusFilter, kpiFilter]);

  const scopedBGs = useMemo(() => {
    let bgs = [...bankGuarantees];
    if (projectFilter !== 'all') bgs = bgs.filter((b) => b.project_id === parseInt(projectFilter, 10));
    if (typeFilter !== 'all') bgs = bgs.filter((b) => normalizeBGType(b.bg_type) === typeFilter);
    return bgs;
  }, [bankGuarantees, projectFilter, typeFilter]);

  const bgKpis = useMemo(() => {
    const expiringIn30Days = scopedBGs.filter((bg) => {
      const days = getDaysToExpiry(bg.expiry_date);
      return days >= 0 && days <= 30;
    }).length;

    const expired = scopedBGs.filter((bg) => getDaysToExpiry(bg.expiry_date) < 0).length;
    const renewalRequested = scopedBGs.filter((bg) => bg.status === 'Renewal Requested').length;
    const expiring31To60Days = scopedBGs.filter((bg) => {
      const days = getDaysToExpiry(bg.expiry_date);
      return days > 30 && days <= 60;
    }).length;

    return {
      total: scopedBGs.length,
      expiringIn30Days,
      expiring31To60Days,
      expired,
      renewalRequested,
    };
  }, [scopedBGs]);

  const totalPages = Math.max(1, Math.ceil(filteredBGs.length / perPage));
  const paginatedBGs = filteredBGs.slice((page - 1) * perPage, page * perPage);
  const showProjectColumn = projectFilter === 'all';

  const getStatusClass = (status) => BG_STATUS_CLASS_MAP[status] || 'draft';

  if (!dataReady) {
    return (
      <div>
        <div className="section-header"><h2>BG / Insurance Bonds</h2></div>
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading...</p>
      </div>
    );
  }


  const onBankSelect = (setter, bankId) => {
    const bank = banks.find((b) => String(b.id) === String(bankId));
    setter((prev) => ({
      ...prev,
      bank_id: bankId,
      bank_limit_id: '',
      bank: bank?.bank_name || '',
      ifsc: bank?.ifsc_code || '',
      branch: bank?.branch_name || '',
    }));
  };


  const isCountsReadOnly = (form, policy) => {
    if (form.issue_mode === 'AGAINST_BANK_LIMIT' || form.issue_mode === 'PARTIAL_CASH_MARGIN') return true;
    if (form.issue_mode === 'WITHOUT_LIMIT_TRACKING') return true;
    if (form.issue_mode === 'FULL_CASH_BACKED') return policy?.allow_full_cash_backed_limit_override === false;
    return false;
  };

  const renderFinanceFields = (form, setter, policy) => (
    <>
      <div className="form-row">
        <div className="form-group">
          <label>Bank</label>
          <select value={form.bank_id || ''} onChange={(e) => onBankSelect(setter, e.target.value)}>
            <option value="">Select Bank (optional)</option>
            {banks.filter((b) => b.is_active !== false).map((b) => (
              <option key={b.id} value={b.id}>{b.bank_name}</option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label>Bank Limit</label>
          <select
            value={form.bank_limit_id || ''}
            onChange={(e) => setter((prev) => ({ ...prev, bank_limit_id: e.target.value }))}
            disabled={!form.bank_id}
          >
            <option value="">Select Limit</option>
            {bankLimits.map((l) => (
              <option key={l.id} value={l.id}>{l.limit_name || `Limit #${l.id}`}</option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Issue Mode</label>
          <select
            value={form.issue_mode || 'WITHOUT_LIMIT_TRACKING'}
            onChange={(e) => setter((prev) => { const updates = { issue_mode: e.target.value }; const mode = e.target.value; if (mode === 'FULL_CASH_BACKED') { updates.cash_margin_percent = '100'; updates.counts_against_bank_limit = policy?.full_cash_backed_counts_against_limit_default ?? false; } else if (mode === 'AGAINST_BANK_LIMIT' || mode === 'PARTIAL_CASH_MARGIN') { updates.counts_against_bank_limit = true; if (mode === 'AGAINST_BANK_LIMIT') updates.cash_margin_percent = ''; } else { updates.counts_against_bank_limit = false; updates.cash_margin_percent = ''; } return { ...prev, ...updates }; })}
          >
            {ISSUE_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Counts Against Bank Eligibility</label>
          <input
            type="checkbox"
            checked={!!form.counts_against_bank_limit}
            disabled={isCountsReadOnly(form, policy)}
            onChange={(e) => setter((prev) => ({ ...prev, counts_against_bank_limit: e.target.checked }))}
            style={{ width: 'auto', marginTop: 8 }}
          />
          {isCountsReadOnly(form, policy) && (
            <p style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>Set automatically from issue mode / bank policy</p>
          )}
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Cash Margin %</label>
          <input
            type="number"
            value={form.cash_margin_percent ?? ''}
            onChange={(e) => setter((prev) => ({ ...prev, cash_margin_percent: e.target.value }))}
            disabled={form.issue_mode === 'FULL_CASH_BACKED' || form.issue_mode === 'AGAINST_BANK_LIMIT' || form.issue_mode === 'WITHOUT_LIMIT_TRACKING'}
            placeholder={form.issue_mode === 'PARTIAL_CASH_MARGIN' ? 'Required' : 'Optional'}
          />
        </div>
        <div className="form-group">
          <label>Branch</label>
          <input type="text" value={form.branch || ''} onChange={(e) => setter((prev) => ({ ...prev, branch: e.target.value }))} />
        </div>
      </div>
    </>
  );

  const handleCreateBG = async () => {
    if (isSubmitting) return;

    if (!newBG.project_id || !newBG.bg_no.trim() || !newBG.expiry_date) {
      notifyWarning('Project, BG Number and ValidUpto are required.');
      return;
    }

    const exists = bankGuarantees.some((bg) => (
      String(bg.project_id) === String(newBG.project_id)
      && String(bg.bg_no || '').trim().toUpperCase() === newBG.bg_no.trim().toUpperCase()
    ));
    if (exists) {
      notifyWarning('This BG / Bond number already exists for the selected project.');
      return;
    }
    if (!(await confirmDialog('Register this Bank Guarantee / Bond?', 'New BG / Bond'))) return;

    setIsSubmitting(true);
    try {
      const payload = buildBgPayload({ ...newBG, status: 'Active' });
      const response = await bankGuaranteesAPI.create(payload);
      const mapped = mapBgRow(response?.data);
      if (mapped) setBankGuarantees((prev) => [mapped, ...prev]);
      if (formConfig.customFieldsEnabled && mapped?.id) {
        try {
          await adminAPI.saveCustomFieldValues('BankGuarantee', mapped.id, {
            pageKey: 'bank_guarantees',
            projectId: Number(newBG.project_id),
            values: customValues,
            mode: 'create',
          });
        } catch (cvErr) {
          console.error('Custom field save failed:', cvErr);
          notifyError(cvErr?.response?.data?.message || 'BG saved but custom fields failed.');
        }
      }
      setShowModal(false);
      setNewBG(emptyNewBG());
      setCustomValues({});
      notifySuccess('BG / Insurance Bond registered successfully.');
    } catch (error) {
      console.error('Create BG failed:', error);
      notifyError(error?.response?.data?.message || 'Unable to create BG / Insurance Bond.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setNewBG((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const openEditModal = (bg) => {
    setSelectedBG(null);
    setEditBG({
      id: bg.id,
      project_id: String(bg.project_id),
      bg_type: normalizeBGType(bg.bg_type),
      bg_no: bg.bg_no || '',
      amount: bg.amount != null && bg.amount !== '' ? String(bg.amount) : '',
      bank: bg.bank && bg.bank !== '-' ? bg.bank : '',
      ifsc: bg.ifsc && bg.ifsc !== '-' ? bg.ifsc : '',
      branch: bg.branch && bg.branch !== '-' ? bg.branch : '',
      issue_date: bg.issue_date || '',
      expiry_date: bg.expiry_date || '',
      claim_expiry_date: bg.claim_expiry_date || bg.expiry_date || '',
      status: bg.status || 'Active',
      attachments: [...(bg.attachments || [])],
      linked_correspondence: bg.linked_correspondence ?? null,
      renewed_from_bg_id: bg.renewed_from_bg_id ?? null,
      purpose: bg.purpose || '',
      drawn_in_favour_of: bg.drawn_in_favour_of || '',
      remarks: bg.remarks || '',
      bank_id: bg.bank_id ? String(bg.bank_id) : '',
      bank_limit_id: bg.bank_limit_id ? String(bg.bank_limit_id) : '',
      issue_mode: bg.issue_mode || 'WITHOUT_LIMIT_TRACKING',
      counts_against_bank_limit: !!bg.counts_against_bank_limit,
      cash_margin_percent: bg.cash_margin_percent != null ? String(bg.cash_margin_percent) : '',
    });
    setEditCustomValues(defaultsFromCustomFields());
    if (formConfig.customFieldsEnabled) {
      adminAPI.getCustomFieldValues('BankGuarantee', bg.id)
        .then((res) => {
          setEditCustomValues({
            ...defaultsFromCustomFields(),
            ...(res?.data?.data?.values || res?.data?.values || {}),
          });
        })
        .catch(() => { /* keep defaults */ });
    }
    setShowEditModal(true);
  };

  const handleEditAttachmentChange = async (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    setEditAttachmentUploading(true);
    try {
      const newFiles = await toStoredAttachments(files);
      setEditBG((prev) => ({ ...prev, attachments: [...(prev.attachments || []), ...newFiles] }));
    } finally {
      setEditAttachmentUploading(false);
    }
    event.target.value = '';
  };

  const handleUpdateBG = async () => {
    if (isEditSubmitting || !editBG.id) return;
    if (!editBG.project_id || !editBG.bg_no.trim() || !editBG.expiry_date) {
      notifyWarning('Project, BG Number and ValidUpto are required.');
      return;
    }
    const dup = bankGuarantees.some((bg) => (
      bg.id !== editBG.id
      && String(bg.project_id) === String(editBG.project_id)
      && String(bg.bg_no || '').trim().toUpperCase() === editBG.bg_no.trim().toUpperCase()
    ));
    if (dup) {
      notifyWarning('This BG / Bond number already exists for the selected project.');
      return;
    }
    if (!(await confirmDialog('Save changes to this BG / Insurance Bond?', 'Update BG / Bond'))) return;

    setIsEditSubmitting(true);
    try {
      const payload = buildBgPayload(editBG);
      const response = await bankGuaranteesAPI.update(editBG.id, payload);
      const mapped = mapBgRow(response?.data);
      if (mapped) {
        setBankGuarantees((prev) => prev.map((b) => (b.id === mapped.id ? mapped : b)));
      }
      if (formConfig.customFieldsEnabled) {
        try {
          await adminAPI.saveCustomFieldValues('BankGuarantee', editBG.id, {
            pageKey: 'bank_guarantees',
            projectId: Number(editBG.project_id),
            values: editCustomValues,
            mode: 'edit',
          });
        } catch (cvErr) {
          console.error('Custom field save failed:', cvErr);
          notifyError(cvErr?.response?.data?.message || 'BG updated but custom fields failed.');
        }
      }
      setShowEditModal(false);
      setEditBG(emptyEditBG());
      setEditCustomValues({});
      notifySuccess('BG / Insurance Bond updated.');
    } catch (error) {
      console.error('Update BG failed:', error);
      notifyError(error?.response?.data?.message || 'Unable to update BG / Insurance Bond.');
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleDeleteBG = async (bg) => {
    if (!bg?.id) return;
    if (!(await confirmDialog(
      `Delete ${bg.bg_no}? This cannot be undone.`,
      'Delete BG / Bond',
    ))) return;
    try {
      await bankGuaranteesAPI.delete(bg.id);
      setBankGuarantees((prev) => prev.filter((b) => b.id !== bg.id));
      if (selectedBG?.id === bg.id) setSelectedBG(null);
      if (editBG?.id === bg.id) {
        setShowEditModal(false);
        setEditBG(emptyEditBG());
      }
      notifySuccess('BG / Insurance Bond removed.');
    } catch (error) {
      console.error('Delete BG failed:', error);
      notifyError(error?.response?.data?.message || 'Unable to delete BG / Insurance Bond.');
    }
  };


  const registerExportSubtitle = buildBgExportSubtitle([
    projectFilter === 'all' ? 'All Projects' : (projects.find((p) => p.id === parseInt(projectFilter, 10))?.name || 'Selected Project'),
    typeFilter !== 'all' ? `Type: ${typeFilter}` : null,
    statusFilter !== 'all' ? `Status: ${statusFilter}` : null,
    `${filteredBGs.length} record(s)`,
  ]);

  const buildRegisterExportData = () => {
    const headers = [
      'BG No',
      ...(showProjectColumn ? ['Project'] : []),
      'Type', 'Drawn in favour of', 'Bank', 'Issue Mode', 'Amount', 'Cash Margin',
      'Counts Limit', 'Rule', 'Expiry Date', 'Claim Expiry', 'Days to Expiry',
      'Renewal Status', 'Renewal Count', 'Last Renewed', 'Renewed By', 'Status',
    ];
    const amountColumnIndices = showProjectColumn ? [6, 7] : [5, 6];
    const rows = filteredBGs.map((bg) => {
      const project = projects.find((pr) => pr.id === bg.project_id);
      const daysLeft = getDaysToExpiry(bg);
      return [
        bg.bg_no,
        ...(showProjectColumn ? [project?.name || ''] : []),
        normalizeBGType(bg.bg_type),
        bg.drawn_in_favour_of || '',
        bg.bank || '',
        ISSUE_MODE_LABELS[bg.issue_mode] || bg.issue_mode || '',
        bg.amount,
        bg.cash_margin_amount || 0,
        bg.counts_against_bank_limit ? 'Yes' : 'No',
        RULE_SOURCE_LABELS[bg.counts_against_limit_rule_source] || '',
        bg.current_expiry_date || bg.expiry_date || '',
        bg.claim_expiry_date || '',
        daysLeft == null ? '' : (daysLeft > 0 ? `${daysLeft} days` : 'Expired'),
        bg.renewal_status_label || RENEWAL_STATUS_LABELS[bg.renewal_status] || '',
        bg.renewal_count ?? 0,
        bg.last_renewed_at || '',
        bg.last_renewed_by?.name || '',
        bg.status || '',
      ];
    });
    return { headers, rows, amountColumnIndices };
  };

  const handleRegisterExportPdf = () => {
    const { headers, rows, amountColumnIndices } = buildRegisterExportData();
    exportDataRowsPdf({
      title: 'BG / Insurance Bonds Register',
      subtitle: registerExportSubtitle,
      headers,
      rows,
      amountColumnIndices,
      emptyMessage: 'No BG / Insurance Bonds found.',
    });
  };

  const handleRegisterExportCsv = () => {
    const { headers, rows } = buildRegisterExportData();
    exportDataRowsCsv({ headers, rows, filename: 'bg-bond-register.csv' });
  };

  return (
    <div>
      <div className="section-header">
        <h2>BG / Insurance Bonds Register <span className="count-badge">{filteredBGs.length}</span></h2>
        <div style={{ display: 'flex', gap: 8 }}>
          <BgExportButtons onDownloadPdf={handleRegisterExportPdf} onDownloadCsv={handleRegisterExportCsv} />
          <button className="btn-primary" onClick={() => { setCustomValues(defaultsFromCustomFields()); setShowModal(true); }}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New BG / Bond
          </button>
        </div>
      </div>

      <div className="filter-bar">
        <ProjectFilterSelect
          value={projectFilter}
          onChange={(value) => { setProjectFilter(value); setPage(1); }}
          projects={projects}
        />
        <select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setPage(1); }}>
          <option value="all">All Types</option>
          <option value="Bank Guarantee">Bank Guarantee</option>
          <option value="Insurance Bond">Insurance Bond</option>
        </select>
        <select value={bankFilter} onChange={(e) => { setBankFilter(e.target.value); setPage(1); }}>
          <option value="all">All Banks</option>
          {banks.map((b) => <option key={b.id} value={b.id}>{b.bank_name}</option>)}
        </select>
        <select value={issueModeFilter} onChange={(e) => { setIssueModeFilter(e.target.value); setPage(1); }}>
          <option value="all">All Issue Modes</option>
          {ISSUE_MODE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <select value={countsFilter} onChange={(e) => { setCountsFilter(e.target.value); setPage(1); }}>
          <option value="all">All Limit Consumption</option>
          <option value="yes">Counts Against Limit</option>
          <option value="no">Does Not Count</option>
        </select>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}>
          <option value="all">All Status</option>
          <option value="Active">Active</option>
          <option value="Expiring">Expiring</option>
          <option value="Renewal Requested">Renewal Requested</option>
          <option value="Renewed">Renewed</option>
          <option value="Released">Released</option>
          <option value="Invoked">Invoked</option>
          <option value="Closed">Closed</option>
        </select>
        <ResetFiltersButton
          onClick={() => {
            setProjectFilter('all');
            setBankFilter('all');
            setIssueModeFilter('all');
            setCountsFilter('all');
            setTypeFilter('all');
            setStatusFilter('all');
            setKpiFilter('all');
            setPage(1);
          }}
        />
      </div>

      <div className="cards-grid kpi-cards" style={{ marginBottom: 16 }}>
        <div className="card" onClick={() => { setKpiFilter('all'); setPage(1); }} style={{ cursor: 'pointer', borderColor: kpiFilter === 'all' ? '#93c5fd' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Total Instruments</p>
          <p className="kpi-value">{bgKpis.total}</p>
        </div>
        <div className="card" onClick={() => { setKpiFilter('expiring30'); setPage(1); }} style={{ cursor: 'pointer', borderColor: kpiFilter === 'expiring30' ? '#f59e0b' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Expiring in 30 Days</p>
          <p className="kpi-value">{bgKpis.expiringIn30Days}</p>
        </div>
        <div className="card" onClick={() => { setKpiFilter('expiring31to60'); setPage(1); }} style={{ cursor: 'pointer', borderColor: kpiFilter === 'expiring31to60' ? '#f59e0b' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Expiring in 31-60 Days</p>
          <p className="kpi-value">{bgKpis.expiring31To60Days}</p>
        </div>
        <div className="card" onClick={() => { setKpiFilter('renewalRequested'); setPage(1); }} style={{ cursor: 'pointer', borderColor: kpiFilter === 'renewalRequested' ? '#a78bfa' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Renewal Requested</p>
          <p className="kpi-value">{bgKpis.renewalRequested}</p>
        </div>
        <div className="card" onClick={() => { setKpiFilter('expired'); setPage(1); }} style={{ cursor: 'pointer', borderColor: kpiFilter === 'expired' ? '#fca5a5' : undefined }}>
          <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Already Expired</p>
          <p className="kpi-value">{bgKpis.expired}</p>
        </div>
      </div>

      <div className="table-container" ref={tableRef}>
        <table className="data-table">
          <thead>
            <tr>
              <th>BG No</th>
              {showProjectColumn && <th>Project</th>}
              <th>Type</th>
              <th>Drawn in favour of</th>
              <th>Bank</th>
              <th>Issue Mode</th>
              <th className="amount-cell">Amount</th>
              <th className="amount-cell">Cash Margin</th>
              <th>Counts Limit</th>
              <th>Rule</th>
              <th>Expiry Date</th>
              <th>Claim Expiry</th>
              <th>Days to Expiry</th>
              <th>Renewal Status</th>
              <th>Renewal Count</th>
              <th>Last Renewed</th>
              <th>Renewed By</th>
              <th>Status</th>
              <th className="bg-export-exclude" style={{ width: 180 }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedBGs.length === 0 ? (
              <TableEmptyRow colSpan={showProjectColumn ? 22 : 21} message="No BG / Insurance Bonds found." />
            ) : (
              paginatedBGs.map((bg) => {
                const project = projects.find(p => p.id === bg.project_id);
                const daysLeft = getDaysToExpiry(bg.expiry_date);
                const displayBGType = normalizeBGType(bg.bg_type);
                return (
                  <tr key={bg.id} className="clickable" onClick={() => setSelectedBG(bg)}>
                    <td style={{ fontWeight: 500, color: '#7C3AED' }}>{bg.bg_no}</td>
                    {showProjectColumn && <td style={{ fontSize: 12, color: '#6b7280' }}>{project?.name}</td>}
                    <td><span className={`status-badge ${displayBGType === 'Bank Guarantee' ? 'open' : displayBGType === 'Insurance Bond' ? 'approved' : 'medium'}`}>{displayBGType}</span></td>
                    <td style={{ fontSize: 12, color: '#374151' }}>{bg.drawn_in_favour_of || '—'}</td>
                    <td style={{ fontSize: 12 }}>{bg.bank}</td>
                    <td style={{ fontSize: 11 }}>{ISSUE_MODE_LABELS[bg.issue_mode] || bg.issue_mode || '—'}</td>
                    <td className="amount-cell" style={{ fontWeight: 500 }}>{formatCurrency(bg.amount)}</td>
                    <td className="amount-cell" style={{ fontSize: 12 }}>{formatCurrency(bg.cash_margin_amount || 0)}</td>
                    <td>{bg.counts_against_bank_limit ? 'Yes' : 'No'}</td>
                    <td style={{ fontSize: 10, color: '#6b7280' }}>{RULE_SOURCE_LABELS[bg.counts_against_limit_rule_source] || '—'}</td>
                    <td>{formatDateIN(bg.current_expiry_date || bg.expiry_date)}</td>
                    <td>{formatDateIN(bg.claim_expiry_date)}</td>
                    <td>
                      <span style={{
                        fontWeight: 500,
                        color: daysLeft != null && daysLeft <= 7 ? '#b91c1c' : daysLeft != null && daysLeft <= 30 ? '#b45309' : '#047857'
                      }}>
                        {daysLeft == null ? '—' : daysLeft > 0 ? `${daysLeft} days` : 'Expired'}
                      </span>
                    </td>
                    <td><StatusBadge tone={renewalStatusTone(bg.renewal_status)}>{bg.renewal_status_label || RENEWAL_STATUS_LABELS[bg.renewal_status] || bg.renewal_status || '—'}</StatusBadge></td>
                    <td style={{ position: 'relative' }}>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        title="View renewal history"
                        onClick={(e) => { e.stopPropagation(); setHistoryPopup(historyPopup?.id === bg.id ? null : { id: bg.id, bg_no: bg.bg_no }); }}
                      >
                        {bg.renewal_count ?? 0}
                      </button>
                      {historyPopup?.id === bg.id && (
                        <RenewalHistoryPopup bgId={bg.id} bgNo={bg.bg_no} onClose={() => setHistoryPopup(null)} anchorStyle={{ top: '100%', left: 0, marginTop: 4 }} />
                      )}
                    </td>
                    <td>{formatDateIN(bg.last_renewed_at)}</td>
                    <td style={{ fontSize: 12 }}>{bg.last_renewed_by?.name || '—'}</td>
                    <td><StatusBadge tone={getStatusClass(bg.status)}>{bg.status}</StatusBadge></td>
                    <td className="bg-export-exclude" onClick={(e) => e.stopPropagation()} style={{ whiteSpace: 'nowrap' }}>
                      {canShowRenew(bg) && (
                        <>
                          <button type="button" className="btn-primary btn-sm" onClick={() => setRenewTarget(bg)}>Renew</button>
                          {' '}
                        </>
                      )}
                      <button type="button" className="btn-secondary btn-sm" onClick={() => openEditModal(bg)}>Edit</button>
                      {' '}
                      <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => handleDeleteBG(bg)}>Delete</button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
        <TablePagination
          page={page}
          totalPages={totalPages}
          onPrevious={() => setPage(page - 1)}
          onNext={() => setPage(page + 1)}
        />
      </div>

      {selectedBG && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>BG Detail - {selectedBG.bg_no}</h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <button type="button" className="btn-secondary btn-sm" onClick={() => openEditModal(selectedBG)}>Edit</button>
                <button type="button" className="btn-secondary btn-sm" style={{ color: '#b91c1c' }} onClick={() => handleDeleteBG(selectedBG)}>Delete</button>
                <button className="btn-close" onClick={() => setSelectedBG(null)}>
                  <svg viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="modal-body">
              <LinkedCorrespondenceCard linkedCorrespondenceId={selectedBG.linked_correspondence} onViewThread={onViewThread} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>BG / Insurance Type</p><p style={{ fontSize: 14, fontWeight: 500 }}>{normalizeBGType(selectedBG.bg_type)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Status</p><p><StatusBadge tone={getStatusClass(selectedBG.status)}>{selectedBG.status}</StatusBadge></p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Bank</p><p style={{ fontSize: 14 }}>{selectedBG.bank}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Branch</p><p style={{ fontSize: 14 }}>{selectedBG.branch}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>IFSC</p><p style={{ fontSize: 14 }}>{selectedBG.ifsc}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Amount</p><p style={{ fontSize: 14, fontWeight: 600, color: '#7C3AED' }}>{formatCurrency(selectedBG.amount)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Issue Date</p><p style={{ fontSize: 14 }}>{formatDateIN(selectedBG.issue_date)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>ValidUpto</p><p style={{ fontSize: 14, fontWeight: 500, color: getDaysToExpiry(selectedBG.expiry_date) <= 30 ? '#b91c1c' : '#374151' }}>{formatDateIN(selectedBG.expiry_date)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Claim ValidUpto</p><p style={{ fontSize: 14 }}>{formatDateIN(selectedBG.claim_expiry_date)}</p></div>
                <div><p style={{ fontSize: 12, color: '#6b7280' }}>Days to Expiry</p><p style={{ fontSize: 14, fontWeight: 600 }}>{getDaysToExpiry(selectedBG.expiry_date)} days</p></div>
                <div style={{ gridColumn: '1 / -1' }}><p style={{ fontSize: 12, color: '#6b7280' }}>Purpose</p><p style={{ fontSize: 14, margin: 0, whiteSpace: 'pre-wrap' }}>{selectedBG.purpose || '—'}</p></div>
                <div style={{ gridColumn: '1 / -1' }}><p style={{ fontSize: 12, color: '#6b7280' }}>Drawn in favour of</p><p style={{ fontSize: 14, margin: 0, whiteSpace: 'pre-wrap' }}>{selectedBG.drawn_in_favour_of || '—'}</p></div>
                <div style={{ gridColumn: '1 / -1' }}><p style={{ fontSize: 12, color: '#6b7280' }}>Remarks</p><p style={{ fontSize: 14, margin: 0, whiteSpace: 'pre-wrap' }}>{selectedBG.remarks || '—'}</p></div>
              </div>
              <RenewalHistorySection rows={detailRenewals} />
              {(selectedBG.attachments || []).length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1f2937', marginBottom: 8 }}>Attachments</h4>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                    {selectedBG.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: '#374151' }}>{file?.name || `Attachment ${idx + 1}`}</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => viewAttachmentInNewTab(file, () => downloadAttachment(file, `BG attachment: ${file?.name}`))}>View</button>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => downloadAttachment(file, `BG attachment: ${file?.name}`)}>Download</button>
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Edit BG / Insurance Bond</h2>
              <button className="btn-close" onClick={() => { setShowEditModal(false); setEditBG(emptyEditBG()); }}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {!formConfig.fallback && (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>Using published form configuration v{formConfig.config?.versionNumber || '—'}</p>
              )}
              <div className="form-row">
                <ConfiguredField fieldKey="project_id" formConfig={formConfig} defaultLabel="Project" defaultRequired>
                  {({ readOnly }) => (
                    <select value={editBG.project_id} disabled={readOnly} onChange={(e) => setEditBG((prev) => ({ ...prev, project_id: e.target.value }))}><option value="">Select Project</option>{projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
                  )}
                </ConfiguredField>
                <div className="form-group">
                  <label>BG / Insurance Type</label>
                  <select value={editBG.bg_type} onChange={(e) => setEditBG((prev) => ({ ...prev, bg_type: e.target.value }))}>
                    {BG_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="bg_no" formConfig={formConfig} defaultLabel="BG Number" defaultRequired>
                  {({ readOnly }) => (
                    <input type="text" value={editBG.bg_no} disabled={readOnly} onChange={(e) => setEditBG((prev) => ({ ...prev, bg_no: e.target.value }))} />
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="amount" formConfig={formConfig} defaultLabel="Amount">
                  {({ readOnly, placeholder }) => (
                    <input type="number" placeholder={placeholder || '0'} value={editBG.amount} disabled={readOnly} onChange={(e) => setEditBG((prev) => ({ ...prev, amount: e.target.value }))} />
                  )}
                </ConfiguredField>
              </div>
              {renderFinanceFields(editBG, setEditBG, cashPolicy)}
              <div className="form-row">
                <ConfiguredField fieldKey="status" formConfig={formConfig} defaultLabel="Status">
                  {({ readOnly }) => (
                    <select value={editBG.status} disabled={readOnly} onChange={(e) => setEditBG((prev) => ({ ...prev, status: e.target.value }))}>
                      <option value="Active">Active</option>
                      <option value="Expiring">Expiring</option>
                      <option value="Renewal Requested">Renewal Requested</option>
                      <option value="Renewed">Renewed</option>
                      <option value="Released">Released</option>
                      <option value="Invoked">Invoked</option>
                      <option value="Closed">Closed</option>
                    </select>
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="bank" formConfig={formConfig} defaultLabel="Bank Name (display)">
                  {({ readOnly }) => (
                    <input type="text" value={editBG.bank} disabled={readOnly} onChange={(e) => setEditBG((prev) => ({ ...prev, bank: e.target.value }))} />
                  )}
                </ConfiguredField>
                <div className="form-group"><label>IFSC Code</label><input type="text" value={editBG.ifsc} onChange={(e) => setEditBG((prev) => ({ ...prev, ifsc: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Issue Date</label><input type="date" value={editBG.issue_date} onChange={(e) => setEditBG((prev) => ({ ...prev, issue_date: e.target.value }))} /></div>
                <ConfiguredField fieldKey="expiry_date" formConfig={formConfig} defaultLabel="ValidUpto" defaultRequired>
                  {({ readOnly }) => (
                    <input type="date" value={editBG.expiry_date} disabled={readOnly} onChange={(e) => setEditBG((prev) => ({ ...prev, expiry_date: e.target.value }))} />
                  )}
                </ConfiguredField>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Claim ValidUpto</label><input type="date" value={editBG.claim_expiry_date} onChange={(e) => setEditBG((prev) => ({ ...prev, claim_expiry_date: e.target.value }))} /></div>
              </div>
              <ConfiguredField fieldKey="purpose" formConfig={formConfig} defaultLabel="Purpose">
                {({ readOnly }) => (
                  <select value={editBG.purpose || ''} disabled={readOnly} onChange={(e) => setEditBG((prev) => ({ ...prev, purpose: e.target.value }))}>
                    <option value="">Select purpose</option>
                    {PURPOSE_OPTIONS.map((purpose) => <option key={purpose} value={purpose}>{purpose}</option>)}
                  </select>
                )}
              </ConfiguredField>
              <div className="form-group">
                <label>Drawn in favour of</label>
                <input type="text" value={editBG.drawn_in_favour_of || ''} onChange={(e) => setEditBG((prev) => ({ ...prev, drawn_in_favour_of: e.target.value }))} placeholder="Optional" />
              </div>
              <div className="form-group">
                <label>Remarks (if any)</label>
                <textarea rows={3} placeholder="Optional notes" value={editBG.remarks} onChange={(e) => setEditBG((prev) => ({ ...prev, remarks: e.target.value }))} style={{ width: '100%', resize: 'vertical', minHeight: 64 }} />
              </div>
              <div className="form-group">
                <label>Attachments</label>
                <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleEditAttachmentChange} disabled={editAttachmentUploading} />
                {editAttachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {editBG.attachments?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {editBG.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '4px 8px' }}>
                        {file?.name}
                        <button type="button" className="btn-secondary btn-sm" onClick={() => setEditBG((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, i) => i !== idx) }))}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {formConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={customFields}
                  values={editCustomValues}
                  onChange={onEditCustomChange}
                  title="Additional fields"
                />
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => { setShowEditModal(false); setEditBG(emptyEditBG()); setEditCustomValues({}); }} disabled={isEditSubmitting || editAttachmentUploading}>Cancel</button>
                <button className="btn-primary" onClick={handleUpdateBG} disabled={isEditSubmitting || editAttachmentUploading}>{isEditSubmitting ? 'Saving...' : 'Save changes'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h2>Add New BG / Insurance Bond</h2>
              <button className="btn-close" onClick={() => setShowModal(false)}>
                <svg viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <div className="modal-body">
              {!formConfig.fallback && (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 0 }}>Using published form configuration v{formConfig.config?.versionNumber || '—'}</p>
              )}
              <div className="form-row">
                <ConfiguredField fieldKey="project_id" formConfig={formConfig} defaultLabel="Project" defaultRequired>
                  {({ readOnly }) => (
                    <select value={newBG.project_id} disabled={readOnly} onChange={(e) => setNewBG((prev) => ({ ...prev, project_id: e.target.value }))}><option value="">Select Project</option>{projects.filter(p => p.status === 'Active').map(p => <option key={p.id} value={p.id}>{p.name}</option>)}</select>
                  )}
                </ConfiguredField>
                <div className="form-group">
                  <label>BG / Insurance Type</label>
                  <select value={newBG.bg_type} onChange={(e) => setNewBG((prev) => ({ ...prev, bg_type: e.target.value }))}>
                    {BG_TYPE_OPTIONS.map((type) => <option key={type} value={type}>{type}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <ConfiguredField fieldKey="bg_no" formConfig={formConfig} defaultLabel="BG Number" defaultRequired>
                  {({ readOnly, placeholder }) => (
                    <input type="text" placeholder={placeholder || 'BG/2026/...'} value={newBG.bg_no} disabled={readOnly} onChange={(e) => setNewBG((prev) => ({ ...prev, bg_no: e.target.value }))} />
                  )}
                </ConfiguredField>
                <ConfiguredField fieldKey="amount" formConfig={formConfig} defaultLabel="Amount">
                  {({ readOnly, placeholder }) => (
                    <input type="number" placeholder={placeholder || '0'} value={newBG.amount} disabled={readOnly} onChange={(e) => setNewBG((prev) => ({ ...prev, amount: e.target.value }))} />
                  )}
                </ConfiguredField>
              </div>
              {renderFinanceFields(newBG, setNewBG, cashPolicy)}
              <div className="form-row">
                <ConfiguredField fieldKey="bank" formConfig={formConfig} defaultLabel="Bank Name (display)">
                  {({ readOnly }) => (
                    <input type="text" value={newBG.bank} disabled={readOnly} onChange={(e) => setNewBG((prev) => ({ ...prev, bank: e.target.value }))} />
                  )}
                </ConfiguredField>
                <div className="form-group"><label>IFSC Code</label><input type="text" value={newBG.ifsc} onChange={(e) => setNewBG((prev) => ({ ...prev, ifsc: e.target.value }))} /></div>
              </div>
              <div className="form-row">
                <div className="form-group"><label>Issue Date</label><input type="date" value={newBG.issue_date} onChange={(e) => setNewBG((prev) => ({ ...prev, issue_date: e.target.value }))} /></div>
                <ConfiguredField fieldKey="expiry_date" formConfig={formConfig} defaultLabel="ValidUpto" defaultRequired>
                  {({ readOnly }) => (
                    <input type="date" value={newBG.expiry_date} disabled={readOnly} onChange={(e) => setNewBG((prev) => ({ ...prev, expiry_date: e.target.value }))} />
                  )}
                </ConfiguredField>
              </div>
              <ConfiguredField fieldKey="purpose" formConfig={formConfig} defaultLabel="Purpose">
                {({ readOnly }) => (
                  <select value={newBG.purpose || ''} disabled={readOnly} onChange={(e) => setNewBG((prev) => ({ ...prev, purpose: e.target.value }))}>
                    <option value="">Select purpose</option>
                    {PURPOSE_OPTIONS.map((purpose) => <option key={purpose} value={purpose}>{purpose}</option>)}
                  </select>
                )}
              </ConfiguredField>
              <div className="form-group">
                <label>Drawn in favour of</label>
                <input type="text" placeholder="Optional" value={newBG.drawn_in_favour_of || ''} onChange={(e) => setNewBG((prev) => ({ ...prev, drawn_in_favour_of: e.target.value }))} />
              </div>
              <div className="form-group">
                <label>Remarks (if any)</label>
                <textarea rows={3} placeholder="Optional notes" value={newBG.remarks} onChange={(e) => setNewBG((prev) => ({ ...prev, remarks: e.target.value }))} style={{ width: '100%', resize: 'vertical', minHeight: 64 }} />
              </div>
              <div className="form-group">
                <label>Attachments</label>
                <input type="file" multiple accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png" onChange={handleAttachmentChange} disabled={attachmentUploading} />
                {attachmentUploading && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 6, fontSize: 13, color: '#6b7280' }}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, border: '2px solid #e5e7eb', borderTopColor: '#3b82f6', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
                    Processing upload...
                  </span>
                )}
                {newBG.attachments?.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                    {newBG.attachments.map((file, idx) => (
                      <span key={idx} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 6, padding: '4px 8px' }}>
                        {file?.name}
                        <span style={{ color: '#22c55e' }}>✓</span>
                        <button type="button" className="btn-secondary btn-sm" onClick={() => setNewBG((prev) => ({ ...prev, attachments: (prev.attachments || []).filter((_, i) => i !== idx) }))}>✕</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {formConfig.customFieldsEnabled && (
                <DynamicFieldsSection
                  fields={customFields}
                  values={customValues}
                  onChange={onCustomChange}
                  title="Additional fields"
                />
              )}
              <div className="modal-actions">
                <button className="btn-secondary" onClick={() => { setShowModal(false); setCustomValues({}); }} disabled={isSubmitting || attachmentUploading}>Cancel</button>
                <button className="btn-primary" onClick={handleCreateBG} disabled={isSubmitting || attachmentUploading}>{isSubmitting ? 'Creating...' : 'Create'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {renewTarget && (
        <BgRenewModal
          bg={renewTarget}
          onClose={() => setRenewTarget(null)}
          onRenewed={(row) => {
            const mapped = mapBgRow(row);
            if (mapped) setBankGuarantees((prev) => prev.map((b) => (b.id === mapped.id ? mapped : b)));
            if (selectedBG?.id === mapped?.id) setSelectedBG(mapped);
          }}
        />
      )}
    </div>
  );
};

export default BgBondRegister;
